import React from 'react'
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import Axios from 'axios';
import { getAuthHeader } from '../../../utils/authToken';


const AdminActionAccordion = ({
    courseId,
    setAnnouncementsUpdated,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [expanded, setExpanded] = React.useState(false);
    const [title, setTitle] = React.useState("");
    const [author, setAuthor] = React.useState("");
    const [content, setContent] = React.useState("");
    const [templates, setTemplates] = React.useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = React.useState("");

    React.useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/classroom-templates?category=announcement&is_active=true`,
                    { headers: { Authorization: getAuthHeader() } }
                );
                // Exclude the "default" welcome template since that's auto-created
                setTemplates((res.data || []).filter(t => t.template_key !== "default"));
            } catch (err) {
                // silently fail — templates are optional
            }
        };
        fetchTemplates();
    }, []);

    const handleTemplateSelect = async (templateId) => {
        if (!templateId) {
            setSelectedTemplateId("");
            setTitle("");
            setAuthor("");
            setContent("");
            return;
        }

        setSelectedTemplateId(templateId);
        const tmpl = templates.find(t => t.id === templateId);
        if (!tmpl) return;

        try {
            const studentsRes = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/students`,
                { headers: { Authorization: getAuthHeader() } }
            ).catch(() => ({ data: [] }));

            let studentName = "((Student's Name))";
            if (studentsRes.data && studentsRes.data.length === 1) {
                studentName = studentsRes.data[0].student?.first_name || studentName;
            }

            const filledContent = (tmpl.content || "").replace(/\{\{student_name\}\}/g, studentName);
            setTitle(tmpl.title || "");
            setAuthor(tmpl.author || "");
            setContent(filledContent);
        } catch (err) {
            setErrorAlertMessage("Failed to load template");
            setErrorAlertOpen(true);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        let title = event.target.title.value;
        let author = event.target.author.value;
        let content = event.target.content.value;

        if (title === "" || author === "" || content === "") {
            setErrorAlertMessage("Please fill in all fields.");
            setErrorAlertOpen(true);
            return;
        }

        setBackdropOpen(true);

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/announcements`, {
            title: title,
            author: author,
            content: content,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((res) => {
            setSuccessAlertMessage(res.data.message);
            setSuccessAlertOpen(true);
            setBackdropOpen(false);
            setAnnouncementsUpdated(true);
            setExpanded(!expanded);
            setTitle("");
            setAuthor("");
            setContent("");
            setSelectedTemplateId("");

            Axios.post(`${process.env.REACT_APP_URL}/api/v1/mails/courses/${courseId}/announcements`, {
                title: title,
                author: author,
                content: content,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).catch((err) => {
                setErrorAlertMessage(err.response?.data?.message || err.response?.data?.error || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }).catch((err) => {
            setErrorAlertMessage(err.response?.data?.message || err.response?.data?.error || "Something went wrong");
            setErrorAlertOpen(true);
        });
    };

    return (
        <div style={{
            width: "100%",
            marginBottom: "1rem",
        }}>
            <Accordion
                style={{
                    backgroundColor: colors.primary[750],
                }}
                component="div"
                expanded={expanded}
                aria-expanded={expanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    onClick={() => setExpanded(!expanded)}
                >
                    <Typography variant='h6'>Create an announcement</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                        component="form"
                        sx={{
                            '& > :not(style)': { m: 1, width: '25ch' },
                            display: "flex",
                            flexDirection: "column",
                            mr: "1rem",
                        }}
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit}
                    >
                        {templates.length > 0 && (
                            <FormControl variant="filled" sx={{ minWidth: "100%" }}>
                                <InputLabel>Use Template</InputLabel>
                                <Select
                                    value={selectedTemplateId}
                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                    sx={{
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: colors.primary[600],
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>None (blank form)</em>
                                    </MenuItem>
                                    {templates.map((tmpl) => (
                                        <MenuItem key={tmpl.id} value={tmpl.id}>
                                            {tmpl.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            minWidth: "100%",
                        }}>
                            <TextField
                                id="filled-basic"
                                label="Title"
                                variant="filled"
                                name='title'
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                sx={{
                                    flex: "1",
                                    maxWidth: "50%",
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                id="filled-basic"
                                label="Author"
                                variant="filled"
                                name='author'
                                value={author}
                                onChange={(e) => setAuthor(e.target.value)}
                                sx={{
                                    flex: "1",
                                    maxWidth: "50%",
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <TextField
                            id="filled-basic"
                            label="Content"
                            variant="filled"
                            name='content'
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            fullWidth={true}
                            multiline={true}
                            rows={20}
                            sx={{
                                minWidth: "100%",
                                borderRadius: "5px",
                                '& .MuiFilledInput-root': {
                                    backgroundColor: colors.primary[600],
                                    ":focus": {
                                        backgroundColor: 'white',
                                    },
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            style={{
                                backgroundColor: colors.primary[300],
                                color: "white",
                                float: "right",
                                marginTop: "0.5rem",
                                marginBottom: "1rem",
                                width: "10rem",
                            }}
                        >
                            Create
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </div>
    )
}

export default AdminActionAccordion
